import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface PortfolioSummaryData {
  id: string;
  user_id: string;
  total_value: number | null;
  total_invested: number | null;
  total_pnl: number | null;
  total_pnl_pct: number | null;
  daily_pnl: number | null;
  daily_pnl_pct: number | null;
  holdings_count: number | null;
  top_gainer_symbol: string | null;
  top_gainer_pnl_pct: number | null;
  top_loser_symbol: string | null;
  top_loser_pnl_pct: number | null;
  sector_allocation: Record<string, number> | null;
  asset_type_allocation: Record<string, number> | null;
  last_computed_at: string | null;
}

export function usePortfolioSummary() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const summaryQuery = useQuery({
    queryKey: ['portfolio-summary', user?.id],
    queryFn: async (): Promise<PortfolioSummaryData | null> => {
      if (!user) return null;
      const { data, error } = await supabase
        .from('portfolio_summary')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      if (error) throw error;
      return data as PortfolioSummaryData | null;
    },
    enabled: !!user,
    staleTime: 60000,
  });

  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel('portfolio-summary-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'portfolio_summary', filter: `user_id=eq.${user.id}` }, () => {
        queryClient.invalidateQueries({ queryKey: ['portfolio-summary', user.id] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, queryClient]);

  return {
    summary: summaryQuery.data || null,
    isLoading: summaryQuery.isLoading,
  };
}
